// PaintPass.js
// Features:
// - Custom post-processing shader pass for a "painterly" look
// - Quantizes colors and applies a watercolor texture overlay
// - Applies Uncharted 2 tonemapping for filmic color response

import { ShaderPass } from 'postprocessing'
import * as THREE from 'three'

// Credits to Maxime Heckel
// Shader adapted from https://blog.maximeheckel.com/posts/on-crafting-painterly-shaders/

const vertexShader = `
    varying vec2 vUv;
    
    void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
`

const fragmentShader = `
    uniform sampler2D inputBuffer;
    uniform sampler2D watercolorTexture;
    varying vec2 vUv;

    vec3 Uncharted2Helper(vec3 x) {
        float A = 0.15;
        float B = 0.50;
        float C = 0.10;
        float D = 0.20;
        float E = 0.02;
        float F = 0.30;
        return ((x * (A * x + C * B) + D * E) /
                (x * (A * x + B)     + D * F)) - E / F;
    }

    vec3 Uncharted2Tonemap(vec3 color) {
        float exposureBias = 5.0;
        color *= exposureBias;

        vec3 curr = Uncharted2Helper(color);
        vec3 whiteScale = 1.0 / Uncharted2Helper(vec3(11.2)); // white point

        return curr * whiteScale;
    }

    vec3 sat(vec3 rgb, float adjustment) {
        vec3 W = vec3(0.2125, 0.7154, 0.0721); // Luminance weights
        vec3 intensity = vec3(dot(rgb, W));
        return mix(intensity, rgb, adjustment);
    }

    void main() {
        vec4 initialColor = texture2D(inputBuffer, vUv);
        vec3 color = sRGBTransferOETF(initialColor).rgb;
        vec4 watercolorColor = texture2D(watercolorTexture, vUv);
        vec3 grayscale = vec3(dot(color, vec3(0.2, 0.5, 0.1)));

        // Color quantization
        int n = 16;
        float x = grayscale.r;
        float qn = floor(x * float(n - 1) + 0.5) / float(n - 1);
        qn = clamp(qn, 0.2, 0.7);

        if (qn < 0.5) {
            color = mix(vec3(0.03), color.rgb, qn * 2.0);
        } else {
            color = mix(color.rgb, vec3(1.0), (qn - 0.5) * 2.0);
        }

        color = sat(color, 1.5);
        color = Uncharted2Tonemap(color);
        
        vec4 outputColor = vec4(color, 1.0);
        outputColor *= watercolorColor;

        gl_FragColor = outputColor;
    }
`

export class PaintPass extends ShaderPass {
    constructor({ watercolorTexture }) {
        const material = new THREE.ShaderMaterial({
            uniforms: {
                inputBuffer: { value: null }, // filled by ShaderPass
                watercolorTexture: { value: watercolorTexture },
            },
            vertexShader,
            fragmentShader,
        })

        super(material, 'inputBuffer')
    }
}
